<template>
  <div>
    <b-sidebar
      id="sidebar-financial-management-payment"
      sidebar-class="sidebar-lg"
      :visible="paymentSidebar.visible"
      bg-variant="white"
      shadow
      backdrop
      no-header
      right
      @shown="onShow"
      @change="(val) => mutatePaymentSidebar({ visible: val })"
      @hidden="clear"
    >
      <template #default="{ hide }">
        <!-- Header -->
        <div
          class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
        >
          <h4>Avaliar solicitação</h4>
          <div>
            <feather-icon
              class="ml-1 cursor-pointer"
              icon="XIcon"
              size="16"
              @click="hide"
            />
          </div>
        </div>
        <template v-if="bill">
          <b-form
            class="m-2"
            @submit.prevent="onSubmit"
            @reset.prevent="resetForm"
          >
            <template v-if="!isExpense">
              <b-row>
                <b-col cols="6" class="mb-1"> Tipo contrato: </b-col>
                <b-col cols="6" class="mb-1">
                  <span v-if="isEmployee">
                    Colaborador
                  </span>
                  <span v-else> 
                    Consultor 
                  </span>
                </b-col>
              </b-row>
              <b-row>
                <b-col cols="6" class="mb-1"> Solicitante: </b-col>
                <b-col cols="6" class="mb-1 text-primary">
                  <b> {{ bill.consultant.name  }} </b>
                </b-col>
              </b-row>
              <b-row class="border-bottom border-top">
                <b-col cols="12" class="mb-1 mt-1">
                  <b>Detalhes da fatura</b>
                </b-col>
                <b-col cols="6" class="mb-1"> Total de entrevista paga: </b-col>
                <b-col
                  cols="6"
                  class="mb-1"
                  data-testid="payment-sidebar-interviews-amount"
                >
                  <span>
                    {{ bill.interviews_amount | toCurrency }}
                  </span>
                </b-col>
                <b-col cols="6" class="mb-1"> Total de assinatura: </b-col>
                <b-col
                  cols="6"
                  class="mb-1"
                  data-testid="payment-sidebar-subscriptions-amount"
                >
                  <span>
                    {{ bill.subscriptions_amount | toCurrency }}
                  </span>
                </b-col>
                <!--
                  ⚠️ Só os saques criados depois da separação por CNPJ têm as três linhas. Nos
                  antigos as colunas vêm NULL e o ajuste da Plataforma está embutido em
                  adjustments_amount: quebrar o total ali mostraria um "Total Futuro Hub" menor
                  do que o que foi de fato pago naquele saque.
                -->
                <template v-if="hasPayingCompanySplit">
                  <b-col cols="6" class="mb-1"> Ajuste plataforma Futuro: </b-col>
                  <b-col
                    cols="6"
                    class="mb-1"
                    data-testid="payment-sidebar-platform-adjustments-amount"
                  >
                    <span>
                      {{ platformAdjustmentsAmount | toCurrency }}
                    </span>
                  </b-col>
                </template>
                <b-col cols="6" class="mb-2" style="font-weight: bold"> Total plataforma Futuro: </b-col>
                <b-col
                  cols="6"
                  class="mb-2"
                  data-testid="payment-sidebar-platform-total-amount"
                >
                  <span>
                    {{ platformTotalAmount | toCurrency }}
                  </span>
                </b-col>
                <b-col cols="6" class="mb-1">
                  Total de solução financeira:
                </b-col>
                <b-col
                  cols="6"
                  class="mb-1"
                  data-testid="payment-sidebar-financial-solutions-amount"
                >
                  <span>
                    {{ bill.financial_solutions_amount | toCurrency }}
                  </span>
                </b-col>
                <template v-if="hasPayingCompanySplit">
                  <b-col cols="6" class="mb-1"> Ajuste Futuro Hub: </b-col>
                  <b-col
                    cols="6"
                    class="mb-1"
                    data-testid="payment-sidebar-hub-adjustments-amount"
                  >
                    <span>
                      {{ hubAdjustmentsAmount | toCurrency }}
                    </span>
                  </b-col>
                </template>
                <template v-else>
                  <b-col cols="6" class="mb-1"> Total de ajuste: </b-col>
                  <b-col
                    cols="6"
                    class="mb-1"
                    data-testid="payment-sidebar-adjustments-amount"
                  >
                    <span>
                      {{ bill.adjustments_amount | toCurrency }}
                    </span>
                  </b-col>
                </template>
                <b-col cols="6" class="mb-2" style="font-weight: bold"> Total Futuro Hub: </b-col>
                <b-col
                  cols="6"
                  class="mb-2"
                  data-testid="payment-sidebar-hub-total-amount"
                >
                  <span>
                    {{ hubTotalAmount | toCurrency }}
                  </span>
                </b-col>
                <!--
                  ⚠️ Só com separação por CNPJ. No saque legado os ajustes estão todos sem
                  `paying_company_id`, então um link filtrando por Futuro Hub abriria uma lista
                  vazia — pior que não ter link, porque sugere que os lançamentos sumiram.

                  A condição de permissão é a mesma do router e do middleware
                  `fixed_financial_menu_role`: esta tela é liberada por `financial_management`, que
                  é outro resource — quem entra aqui não necessariamente entra na lista de ajustes.
                -->
                <b-col
                  v-if="canOpenHubAdjustments"
                  cols="12"
                  class="mb-2 text-right"
                >
                  <b-link
                    :to="hubAdjustmentsRoute"
                    target="_blank"
                    title="Abre a lista de ajustes filtrada por este PN e pela Futuro Hub. A lista não tem filtro de competência."
                    data-testid="payment-sidebar-hub-adjustments-link"
                  >
                    <small>
                      Ver lançamentos de ajuste
                      <feather-icon icon="ExternalLinkIcon" size="12" />
                    </small>
                  </b-link>
                </b-col>
                <template v-if="hasPayingCompanySplit">
                  <b-col cols="6" class="mb-1"> Rolagem e outros ajustes: </b-col>
                  <b-col
                    cols="6"
                    class="mb-1"
                    data-testid="payment-sidebar-other-adjustments-amount"
                  >
                    <span>
                      {{ otherAdjustmentsAmount | toCurrency }}
                    </span>
                  </b-col>
                </template>

                <b-col cols="6" class="mb-1" style="font-weight: bold"> Total da fatura: </b-col>
                <b-col
                  cols="6"
                  class="mb-1"
                  data-testid="payment-sidebar-invoice-total-amount"
                >
                  <span>
                    {{ invoiceTotal | toCurrency }}
                  </span>
                </b-col>
              </b-row>
            </template>
            <template v-if="expenses && expenses.length > 0">
              <b-row class="border-bottom border-top">
                <template v-for="expense in expenses">
                  <b-col cols="6" class="mb-1 mt-1">
                    <b>Despesa:</b>
                  </b-col>
                  <b-col cols="6" class="mb-1 mt-1"> {{ expense.name }} </b-col>
                  <b-col cols="6" class="mb-1">Centro de custo:</b-col>
                  <b-col cols="6" class="mb-1">{{ expense.cost_center.name }} </b-col>
                  <b-col cols="6" class="mb-1">Categoria:</b-col>
                  <b-col cols="6" class="mb-1">{{ expense.cost_center_category.name }} </b-col>
                  <b-col cols="6" class="mb-1">Valor:</b-col>
                  <b-col 
                    cols="6"
                    class="mb-1 text-primary"
                    style="font-weight: bold"
                  >{{ expense.amount | toCurrency }} </b-col>
                </template>
              </b-row>
            </template>
            <b-row class="border-bottom border-top" v-if="hasInvoiceWithdrawals">
                <b-col cols="12" class="mb-1 mt-1">
                  <b>Saques realizados</b>
                </b-col>
                <template v-for="withdrawal in invoiceWithdrawals">
                  <b-col cols="6" class="mb-1">Saque realizado em {{ withdrawal.created_at | onlyDate }}: </b-col>
                  <b-col
                    cols="6"
                    class="mb-1 text-danger"
                    style="font-weight: bold"
                  >
                    {{ withdrawal.amount | toCurrency }}
                  </b-col>
                </template>
              </b-row>
            <b-row class="mt-1">
              <b-col cols="6" class="mb-1"> Data da solicitação: </b-col>
              <b-col cols="6" class="mb-1">
                {{ bill.created_at | onlyDate }}
              </b-col>
              <template v-if="isExpense">
                <b-col cols="6" class="mb-1"> Método de pagamento: </b-col>
                <b-col cols="6" class="mb-1"> {{ expenses[0].payment_type.name }} </b-col>
                <b-col cols="6" class="mb-1"> Chave de pagamento: </b-col>
                <b-col cols="6" class="mb-1" style="overflow-wrap: break-word"> 
                  {{ expenses[0].payment_key }} 
                </b-col>
              </template>
              <template v-else>
                <b-col cols="6" class="mb-1"> Chave Pix: </b-col>
                <b-col cols="6" class="mb-1" style="overflow-wrap: break-word">
                  {{ bill.consultant.franchise_pix }}
                </b-col>
                <b-col cols="6" class="mb-1"> Tipo da chave: </b-col>
                <b-col cols="6" class="mb-1 text-capitalize">
                  {{
                    bill.consultant.franchise_pix
                      ? bill.consultant.franchise_pix_type_key
                      : ""
                  }}
                </b-col>
              </template>
              <b-col cols="6" class="mb-1" style="font-weight: bold">
                Valor a ser aprovado:
              </b-col>
              <b-col
                cols="6"
                class="mb-1 text-primary"
                style="font-weight: bold"
              >
                {{ bill.amount | toCurrency }}
              </b-col>
              <b-col cols="12" class="mb-2" v-if="isExpense">
                <b-form-group
                  label="Nota fiscal (apenas PDF)"
                  label-for="expense-document"
                >
                <b-form-file
                  :disabled="saving.approve || saving.reprove"
                  id="expense-document"
                  accept="application/pdf"
                  placeholder="Clique para anexar..."
                  v-model="document"
                  custom-file-label="Buscar"
                  :class="{ 'is-invalid': v$.document.$error }"
                />
                  <div class="invalid-feedback">
                    <span v-if="v$.document.required.$invalid">
                      Você deve informar uma nota fiscal em formato PDF.
                    </span>
                    <span v-if="v$.document.maxSize.$invalid">
                      O arquivo deve ter tamanho de até 5MB.
                    </span>
                  </div>
                </b-form-group>
              </b-col>
              <b-col cols="6" class="mb-1" v-else>
                <b-button
                  v-if="bill.document_url"
                  variant="link"
                  size="md"
                  no-caret
                  style="padding: 0"
                  @click="download(bill.id)"
                >
                  Ver nota fiscal <feather-icon icon="DownloadIcon" />
                </b-button>
              </b-col>
              <b-col cols="12" class="mb-1">
                <b-form-group label="Comentário" label-for="comment-area">
                  <b-form-textarea
                    id="comment-area"
                    v-model="comment"
                    :class="{ 'is-invalid': v$.$error }"
                  />
                  <div class="invalid-feedback">
                    <span v-if="v$.$error">
                      Você deve informar um comentário
                    </span>
                  </div>
                </b-form-group>
              </b-col>
            </b-row>
            <div class="d-flex mt-2 justify-content-between">
              <b-button
                v-if="!isExpense"
                :disabled="saving.approve || saving.reprove"
                variant="danger"
                type="submit"
                class="payment-buttons"
                @click="status = managmentTypes.REPROVED"
              >
                {{ saving.reprove ? "Aguarde..." : "Reprovar" }}
              </b-button>
              <b-button
                :disabled="saving.approve || saving.reprove"
                variant="success"
                type="submit"
                class="payment-buttons"
                @click="status = managmentTypes.APPROVED"
              >
                {{ saving.approve ? "Aguarde..." : "Aprovar" }}
              </b-button>
            </div>
          </b-form>
        </template>
        <div v-else class="d-flex justify-content-center m-2">
          <b-spinner variant="primary" />
        </div>
      </template>
    </b-sidebar>
  </div>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BButton,
  BFormInput,
  BFormTextarea,
  BCol,
  BRow,
  BSpinner,
  BFormFile,
  BLink
} from "bootstrap-vue";
import { mapActions, mapMutations, mapGetters } from "vuex";
import { useToast } from "vue-toastification/composition";
import useVuelidate from "@vuelidate/core";
import { required, requiredIf } from "@vuelidate/validators";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import Ripple from "vue-ripple-directive";
import * as types from "../store/types";
import * as managmentTypes from "@/constants/financial_management_types";
import {
  ADMIN_CREDENTIALS_STORAGE_KEY,
  canAccessFinancialMenu,
} from "@/constants/auth";
import { getCredentials } from "@/helpers/credentials";
import { FINANCIAL_ADJUSTMENTS, READ_ACTION } from "@/constants/resources";
import { HUB_ID } from "@/constants/paying_companies";

export default {
  components: {
    // BSV
    BButton,
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BCol,
    BRow,
    BSpinner,
    BFormFile,
    BLink
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      managmentTypes,
      bill: undefined,
      invoiceDetails: undefined,
      invoiceWithdrawals: undefined,
      expenses: undefined,
      saving: {
        approve: false,
        reprove: false,
      },
      comment: undefined,
      status: undefined,
      document: undefined
    };
  },
  setup() {
    return { toast: useToast(), v$: useVuelidate() };
  },
  validations() {
    return {
      comment: {
        required: requiredIf(function () {
          return this.status == managmentTypes.REPROVED;
        }),
      },
      document: {
        required: {},
        maxSize: function (file) {
          if (this.document) {
            return file && file.size <= 5000000;
          }
          return true;
        },
      },
    };
  },
  computed: {
    ...mapGetters({
      paymentSidebar: types.PAYMENT_SIDEBAR,
    }),
    invoiceTotal: function () {
      // ⚠️ Continua somando adjustments_amount, que segue sendo o TOTAL de ajustes. A invariante
      // é plataforma + hub + rolagem = adjustments_amount; trocar por uma das partes quebra o
      // "Total da fatura".
      return (
        this.bill.subscriptions_amount +
        this.bill.interviews_amount +
        this.bill.financial_solutions_amount +
        this.bill.adjustments_amount
      );
    },
    // NULL = saque anterior à separação por CNPJ; 0 = de fato zero. A diferença decide o layout,
    // então a checagem é por null/undefined e nunca por truthiness.
    hasPayingCompanySplit: function () {
      return (
        this.bill != null &&
        this.bill.platform_adjustments_amount !== null &&
        this.bill.platform_adjustments_amount !== undefined
      );
    },
    platformAdjustmentsAmount: function () {
      return Number(this.bill?.platform_adjustments_amount) || 0;
    },
    hubAdjustmentsAmount: function () {
      return Number(this.bill?.hub_adjustments_amount) || 0;
    },
    platformTotalAmount: function () {
      return (
        this.bill.subscriptions_amount +
        this.bill.interviews_amount +
        (this.hasPayingCompanySplit ? this.platformAdjustmentsAmount : 0)
      );
    },
    hubTotalAmount: function () {
      // Sem separação o total da Hub continua sendo solução financeira + TODOS os ajustes: é o
      // valor que aquele saque legado de fato pagou.
      return (
        this.bill.financial_solutions_amount +
        (this.hasPayingCompanySplit
          ? this.hubAdjustmentsAmount
          : this.bill.adjustments_amount)
      );
    },
    otherAdjustmentsAmount: function () {
      return (
        this.bill.adjustments_amount -
        this.platformAdjustmentsAmount -
        this.hubAdjustmentsAmount
      );
    },
    /**
     * Espelha `canViewFixedRoleItem` (@core/libs/acl/utils) e `canAccessFixedFinancialRoute`
     * (libs/acl/routeProtection), que espelham o middleware
     * `fixed_financial_menu_role:financial_adjustment:manage,financial_adjustment:read`:
     * role fixa do menu financeiro OU ability CASL no resource.
     *
     * ⚠️ Basta testar `read`: `manage` é curinga no CASL. E o 403 do backend cairia no
     * interceptor de `helpers/api.js`, que DESLOGA em 401/403/419 — link errado custa a sessão.
     */
    canOpenHubAdjustments: function () {
      if (!this.hasPayingCompanySplit || !this.bill?.consultant_id) return false;
      const credentials = getCredentials(ADMIN_CREDENTIALS_STORAGE_KEY);
      return (
        Boolean(canAccessFinancialMenu(credentials?.user?.user_role_id)) ||
        this.$can(READ_ACTION, FINANCIAL_ADJUSTMENTS)
      );
    },
    hubAdjustmentsRoute: function () {
      return {
        name: "financial-adjustments-list",
        // Nomes iguais aos de `searchRules()` no backend: a query string é espelho do corpo do
        // POST /api/financial_adjustments/search.
        query: {
          consultant: String(this.bill.consultant_id),
          paying_company_id: String(HUB_ID),
        },
      };
    },
    hasInvoiceWithdrawals: function () {
      return this.invoiceWithdrawals && 
        this.invoiceWithdrawals.length > 0
    },
    isEmployee: function () {
      return this.bill && 
        this.bill.user && 
        this.bill.user.department_id && 
        this.bill.consultant_id
    },
    isExpense: function () {
      return this.expenses &&
        this.expenses.length > 0 &&
        !this.isEmployee
    }
  },
  methods: {
    ...mapMutations({
      mutatePaymentSidebar: types.MUTATE_PAYMENT_SIDEBAR,
    }),
    ...mapActions({
      getBillDetails: types.GET_BILL_DETAILS,
      storePaymentReview: types.STORE_PAYMENT_REVIEW,
      downloadDocument: types.DOWNLOAD_DOCUMENT,
    }),
    onShow() {
      this.getBillDetails(this.paymentSidebar.id)
        .then((response) => {
          if (response.data.bill && response.data.bill.id) {
            this.bill = response.data.bill;
            this.invoiceDetails = response.data.invoice_details;
            this.invoiceWithdrawals = response.data.invoice_withdrawals;
            this.expenses = response.data.expenses;
          } else {
            throw new DOMException("Conta não encontrada!");
          }
        })
        .catch((err) => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao carregar os detalhes da conta. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
          this.paymentSidebar.visible = false;
        });
    },
    async onSubmit() {
      const isFormCorrect = await this.v$.$validate();
      if (!isFormCorrect) return;
      if (this.status === managmentTypes.REPROVED) {
        this.saving.reprove = true;
      } else {
        this.saving.approve = true;
      }
      const payload = {
        id: this.bill.id,
        status: this.status,
        comment: this.comment,
        document: this.document
      }
      const request = new FormData();
      for (let [key, value] of Object.entries(payload)) {
        if (value && value !== "") {
          request.append(key, value);
        }
      }
      this.storePaymentReview(request)
        .then((response) => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Sucesso",
              text: "Salvo com sucesso!",
              icon: "CoffeeIcon",
              variant: "success",
            },
          });
          this.mutatePaymentSidebar({ visible: false });
          this.paymentSidebar.saveAction();
        })
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              icon: "AlertTriangleIcon",
              text: "Ocorreu um erro ao salvar. Entre em contato com o setor de TI.",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.saving.reprove = false;
          this.saving.approve = false;
        });
    },
    download() {
      this.downloadDocument(this.bill.id)
        .then((response) => {
          setTimeout(() => {
            window.open(response.data, "_blank").focus();
          });
        })
        .catch((error) => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: `Ocorreu um erro ao fazer download. Entre em contato com o setor de TI.`,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
      this.comment = undefined;
    },
    clear() {
      this.bill = undefined;
      this.comment = undefined;
      this.status = undefined;
      this.invoiceDetails = undefined
      this.invoiceWithdrawals = undefined
    },
  },
};
</script>
<style lang="scss">
.sidebar-bottom {
  position: absolute;
  bottom: 0;
  width: -moz-available; /* WebKit-based browsers will ignore this. */
  width: -webkit-fill-available; /* Mozilla-based browsers will ignore this. */
  width: fill-available;
}

.payment-buttons {
  width: 11rem;
}
</style>
